import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent,
  Alert,
  TablePagination,
  Checkbox,
  ListItemText,
  ListItemIcon,
  Autocomplete,
  Tooltip,
  Menu,
  Skeleton
} from '@mui/material';
import {
  Search as SearchIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  PinDrop,
  Tag as TagIcon,
  Work as WorkIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  MoreVert as MoreVertIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import { jobService, Job, JobSearchDto, JobType, Priority } from '../services/jobService';
import { customerService, Customer, CustomerSearchDto } from '../services/customerService';
import { businessService, Business } from '../services/businessService';
import { useAuth } from '../contexts/AuthContext';
import { useBusinessTheme } from '../contexts/BusinessThemeContext';
import { offlineService } from '../services/offlineService';
import { indexedDbService } from '../services/indexedDbService';
import { useJobs } from '../hooks/useJobs';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../config/queryKeys';

const JobsPage: React.FC = () => {
  const { user } = useAuth();
  const { theme } = useBusinessTheme();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [business, setBusiness] = useState<Business | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isOffline, setIsOffline] = useState(!offlineService.getOnlineStatus());
  const [searchDto, setSearchDto] = useState<JobSearchDto>({
    page: 1,
    pageSize: 20
  });
  const [selectedTypes, setSelectedTypes] = useState<JobType[]>([]);
  const [selectedPriorities, setSelectedPriorities] = useState<Priority[]>([]);
  const [selectedCustomers, setSelectedCustomers] = useState<Customer[]>([]);
  const [customerSearchTerm, setCustomerSearchTerm] = useState<string>('');
  const [customerSearchResults, setCustomerSearchResults] = useState<Customer[]>([]);
  const [isSearchingCustomers, setIsSearchingCustomers] = useState(false);
  const [customerSearchTimeout, setCustomerSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // Offline data state
  const [offlineJobs, setOfflineJobs] = useState<Job[]>([]);
  const [offlineTotalCount, setOfflineTotalCount] = useState(0);
  const [offlineLoading, setOfflineLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // TanStack Query hook - only enabled when online
  const {
    data: onlineJobs = [],
    isLoading: jobsLoading,
    error: jobsError
  } = useJobs({
    ...searchDto,
    enabled: !isOffline && !!user?.businessId
  });

  // Determine which data to use based on offline status
  const jobs = isOffline ? offlineJobs : onlineJobs;
  const totalCount = isOffline ? offlineTotalCount : onlineJobs.length;
  const loading = isOffline ? offlineLoading : jobsLoading;

  // Menu state for mobile actions
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedJobForMenu, setSelectedJobForMenu] = useState<Job | null>(null);

  // Handle initial load state
  useEffect(() => {
    if (!jobsLoading && !offlineLoading) {
      setIsInitialLoad(false);
    }
  }, [jobsLoading, offlineLoading]);

  // Load offline data when offline
  useEffect(() => {
    if (isOffline && user?.businessId) {
      loadOfflineJobs();
    }
  }, [isOffline, searchDto, user?.businessId]);

  const loadOfflineJobs = async () => {
    if (!user?.businessId) return;

    try {
      setOfflineLoading(true);
      console.log('[JobsPage] Loading jobs from IndexedDB (offline mode)');

      const jobsData = await indexedDbService.getJobsForBusiness(user.businessId);

      // Apply local filtering
      let filteredJobs = jobsData;

      if (searchDto.searchTerm) {
        const searchTerm = searchDto.searchTerm.toLowerCase();
        filteredJobs = jobsData.filter(job =>
          job.jobNumber?.toLowerCase().includes(searchTerm) ||
          job.customerName?.toLowerCase().includes(searchTerm) ||
          job.jobAddress?.toLowerCase().includes(searchTerm) ||
          job.notes?.toLowerCase().includes(searchTerm)
        );
      }

      if (searchDto.types && searchDto.types.length > 0) {
        filteredJobs = filteredJobs.filter(job =>
          searchDto.types!.includes(job.type as JobType)
        );
      }

      if (searchDto.priorities && searchDto.priorities.length > 0) {
        filteredJobs = filteredJobs.filter(job =>
          searchDto.priorities!.includes(job.priority as Priority)
        );
      }

      if (searchDto.customerIds && searchDto.customerIds.length > 0) {
        filteredJobs = filteredJobs.filter(job =>
          searchDto.customerIds!.includes(job.customerId)
        );
      }

      // Sort by created date (most recent first)
      filteredJobs = filteredJobs.sort((a, b) => {
        const dateA = new Date(a.createdDate || 0);
        const dateB = new Date(b.createdDate || 0);
        return dateB.getTime() - dateA.getTime();
      });

      // Apply pagination
      const startIndex = ((searchDto.page || 1) - 1) * (searchDto.pageSize || 20);
      const endIndex = startIndex + (searchDto.pageSize || 20);
      const paginatedJobs = filteredJobs.slice(startIndex, endIndex);

      setOfflineJobs(paginatedJobs);
      setOfflineTotalCount(filteredJobs.length);
    } catch (err: any) {
      console.error('Error loading offline jobs:', err);
      setError('Failed to load offline jobs');
    } finally {
      setOfflineLoading(false);
      setIsInitialLoad(false);
    }
  };

  // Offline status monitoring
  useEffect(() => {
    const handleConnectivityChange = (event: Event) => {
      const customEvent = event as CustomEvent;
      const newIsOffline = !customEvent.detail.isOnline;
      setIsOffline(newIsOffline);

      // When coming back online, invalidate queries to refetch
      if (!newIsOffline) {
        queryClient.invalidateQueries({ queryKey: queryKeys.jobs.all });
      }
    };

    window.addEventListener('connectivity-changed', handleConnectivityChange);

    return () => {
      window.removeEventListener('connectivity-changed', handleConnectivityChange);
    };
  }, [queryClient]);

  useEffect(() => {
    if (user?.businessId) {
      loadBusiness();
    }
  }, [user?.businessId]);

  // Handle customerId from URL parameter
  useEffect(() => {
    const customerIdFromUrl = searchParams.get('customerId');
    if (customerIdFromUrl && selectedCustomers.length === 0) {
      loadCustomerById(customerIdFromUrl);
    }
  }, [searchParams]);

  // Debounced customer search
  useEffect(() => {
    if (customerSearchTimeout) {
      clearTimeout(customerSearchTimeout);
    }

    if (customerSearchTerm.trim()) {
      const timeout = setTimeout(() => {
        searchCustomers();
      }, 300);
      setCustomerSearchTimeout(timeout);
    } else {
      setCustomerSearchResults([]);
    }

    return () => {
      if (customerSearchTimeout) {
        clearTimeout(customerSearchTimeout);
      }
    };
  }, [customerSearchTerm]);

  // Auto-dismiss success messages
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // Handle query errors
  useEffect(() => {
    if (jobsError) {
      setError('Failed to load jobs');
    }
  }, [jobsError]);

  const searchCustomers = async () => {
    if (!customerSearchTerm.trim()) {
      setCustomerSearchResults([]);
      return;
    }

    setIsSearchingCustomers(true);
    try {
      const searchDto: CustomerSearchDto = {
        searchTerm: customerSearchTerm,
        page: 1,
        pageSize: 20
      };
      const results = await customerService.getCustomers(searchDto);
      setCustomerSearchResults(results);
    } catch (err: any) {
      console.error('Failed to search customers:', err);
      setError(err.message || 'Failed to search customers');
    } finally {
      setIsSearchingCustomers(false);
    }
  };

  const loadCustomerById = async (customerId: string) => {
    try {
      const customer = await customerService.getCustomer(customerId);
      setSelectedCustomers([customer]);
      setSearchDto(prev => ({
        ...prev,
        customerIds: [customer.id],
        page: 1
      }));
      setPage(0);
    } catch (err: any) {
      console.error('Failed to load customer:', err);
    }
  };

  const loadBusiness = async () => {
    try {
      const businessData = await businessService.getBusinesses();
      if (businessData && businessData.length > 0) {
        setBusiness(businessData[0]);
      }
    } catch (err: any) {
      console.error('Failed to load business:', err);
    }
  };

  const handleSearch = (field: keyof JobSearchDto, value: any) => {
    setSearchDto(prev => ({
      ...prev,
      [field]: value === '' || value === null ? undefined : value,
      page: 1
    }));
    setPage(0);
  };

  const handleTypeChange = (types: JobType[]) => {
    setSelectedTypes(types);
    setSearchDto(prev => ({
      ...prev,
      types: types.length > 0 ? types : undefined,
      page: 1
    }));
    setPage(0);
  };

  const handlePriorityChange = (priorities: Priority[]) => {
    setSelectedPriorities(priorities);
    setSearchDto(prev => ({
      ...prev,
      priorities: priorities.length > 0 ? priorities : undefined,
      page: 1
    }));
    setPage(0);
  };

  const handleCustomerChange = (customers: Customer[]) => {
    setSelectedCustomers(customers);
    setSearchDto(prev => ({
      ...prev,
      customerIds: customers.length > 0 ? customers.map(c => c.id) : undefined,
      page: 1
    }));
    setPage(0);
  };

  // Menu handlers for mobile actions
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, job: Job) => {
    setAnchorEl(event.currentTarget);
    setSelectedJobForMenu(job);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedJobForMenu(null);
  };

  const handleMenuAction = (action: string) => {
    if (!selectedJobForMenu) return;

    switch (action) {
      case 'view':
        handleViewJob(selectedJobForMenu.id);
        break;
    }
    handleMenuClose();
  };

  const handleViewJob = (jobId: string) => {
    navigate(`/crm/jobs/${jobId}`);
  };

  const handleViewJobDesktop = (jobId: string) => {
    window.open(`/crm/jobs/${jobId}`, '_blank');
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
    setSearchDto(prev => ({
      ...prev,
      page: newPage + 1
    }));
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    setSearchDto(prev => ({
      ...prev,
      page: 1,
      pageSize: newRowsPerPage
    }));
  };

  if (isInitialLoad) {
    return (
      <Box sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
        {/* Header skeleton */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Skeleton variant="text" width={100} height={40} />
        </Box>

        {/* Search and Filters skeleton */}
        <Card sx={{ mb: 3, backgroundColor: theme.accentColor }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <Skeleton variant="rectangular" width="100%" height={56} />
              </Grid>
              <Grid item xs={12} md={4}>
                <Skeleton variant="rectangular" width="100%" height={56} />
              </Grid>
              <Grid item xs={12} md={4}>
                <Skeleton variant="rectangular" width="100%" height={56} />
              </Grid>
            </Grid>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Skeleton variant="rectangular" width="100%" height={56} />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Table skeleton */}
        <TableContainer component={Paper} sx={{ backgroundColor: theme.accentColor }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Job</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.from({ length: 5 }).map((_, index) => (
                <TableRow key={`skeleton-${index}`}>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1} sx={{ mb: 0.5 }}>
                      <Skeleton variant="circular" width={14} height={14} />
                      <Skeleton variant="text" width={120} height={24} />
                    </Box>
                    <Box sx={{ mt: 0.5 }}>
                      <Skeleton variant="text" width={200} height={20} />
                      <Skeleton variant="text" width={180} height={20} />
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Skeleton variant="rectangular" width={40} height={40} sx={{ borderRadius: 1 }} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>Jobs | MindShare Calendar</title>
      </Helmet>

      <Box sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h4" component="h1">
              Jobs
            </Typography>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Search and Filters */}
        <Card sx={{ mb: 3, backgroundColor: theme.accentColor }}>
          <CardContent>
            <form onSubmit={(e) => e.preventDefault()}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  placeholder="Search jobs..."
                  value={searchDto.searchTerm || ''}
                  onChange={(e) => handleSearch('searchTerm', e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Types</InputLabel>
                  <Select
                    multiple
                    value={selectedTypes}
                    onChange={(e) => handleTypeChange(e.target.value as JobType[])}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                      }
                    }}
                    label="Types"
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((type) => (
                          <Chip
                            key={type}
                            label={jobService.getJobTypeDisplayName(type)}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    )}
                  >
                    {Object.values(JobType).filter(v => typeof v === 'number').map((type) => (
                      <MenuItem key={type} value={type}>
                        <Checkbox checked={selectedTypes.indexOf(type as JobType) > -1} />
                        <ListItemText primary={jobService.getJobTypeDisplayName(type as JobType)} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Priorities</InputLabel>
                  <Select
                    multiple
                    value={selectedPriorities}
                    onChange={(e) => handlePriorityChange(e.target.value as Priority[])}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                      }
                    }}
                    label="Priorities"
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((priority) => (
                          <Chip
                            key={priority}
                            label={jobService.getPriorityDisplayName(priority)}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    )}
                  >
                    {Object.values(Priority).filter(v => typeof v === 'number').map((priority) => (
                      <MenuItem key={priority} value={priority}>
                        <Checkbox checked={selectedPriorities.indexOf(priority as Priority) > -1} />
                        <ListItemText primary={jobService.getPriorityDisplayName(priority as Priority)} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Customer Search Row */}
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {isOffline && (
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Customer search is disabled when offline. You can still filter by previously selected customers.
                  </Alert>
                </Grid>
              )}
              <Grid item xs={12}>
                <Autocomplete
                  fullWidth
                  disabled={isOffline}
                  options={customerSearchResults}
                  loading={isSearchingCustomers}
                  filterOptions={(x) => x}
                  getOptionLabel={(customer) => `${customer.firstName || ''} ${customer.lastName || ''}`}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  value={null}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      const isSelected = selectedCustomers.some(c => c.id === newValue.id);
                      if (isSelected) {
                        handleCustomerChange(selectedCustomers.filter(c => c.id !== newValue.id));
                      } else {
                        handleCustomerChange([...selectedCustomers, newValue]);
                      }
                      setCustomerSearchTerm('');
                    }
                  }}
                  inputValue={customerSearchTerm}
                  onInputChange={(event, newInputValue, reason) => {
                    if (reason === 'input') {
                      setCustomerSearchTerm(newInputValue);
                    }
                  }}
                  noOptionsText={customerSearchTerm ? "No customers found" : "Start typing to search..."}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Customers"
                      placeholder="Search by name, email, phone, or address..."
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, customer) => {
                    const isSelected = selectedCustomers.some(c => c.id === customer.id);
                    return (
                      <Box component="li" {...props}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%' }}>
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body1" fontWeight="medium">
                                {customer.firstName || ''} {customer.lastName || ''}
                              </Typography>
                              {isSelected && (
                                <Chip
                                  label="Selected"
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                  sx={{ fontSize: '0.7rem', height: 20 }}
                                />
                              )}
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                              <EmailIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography variant="body2" color="text.secondary">
                                {customer.email}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                              <PhoneIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography variant="body2" color="text.secondary">
                                {customer.phoneNumber}
                              </Typography>
                            </Box>
                            {(customer.address || customer.city) && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                <LocationIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                <Typography variant="body2" color="text.secondary">
                                  {[customer.address, customer.city].filter(Boolean).join(', ')}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    );
                  }}
                />
                {selectedCustomers.length > 0 && (
                  <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selectedCustomers.map((customer) => (
                      <Chip
                        key={customer.id}
                        label={`${customer.firstName || ''} ${customer.lastName || ''}`}
                        size="small"
                        color="primary"
                        variant="outlined"
                        onDelete={() => {
                          handleCustomerChange(selectedCustomers.filter(c => c.id !== customer.id));
                        }}
                      />
                    ))}
                  </Box>
                )}
              </Grid>
            </Grid>
            </form>
          </CardContent>
        </Card>

        {/* Jobs Table */}
        <TableContainer component={Paper} sx={{ backgroundColor: theme.accentColor, position: 'relative' }}>
          {loading && (
            <Box sx={{ width: '100%', position: 'absolute', top: 0, left: 0, zIndex: 1 }}>
              <Box sx={{ width: '100%', height: 4, bgcolor: 'primary.main', opacity: 0.7 }}>
                <Box
                  sx={{
                    height: '100%',
                    width: '30%',
                    bgcolor: 'primary.light',
                    animation: 'loading 1s ease-in-out infinite',
                    '@keyframes loading': {
                      '0%': { marginLeft: '0%' },
                      '50%': { marginLeft: '70%' },
                      '100%': { marginLeft: '0%' }
                    }
                  }}
                />
              </Box>
            </Box>
          )}
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Job</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <TableRow key={`skeleton-${index}`}>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1} sx={{ mb: 0.5 }}>
                        <Skeleton variant="circular" width={14} height={14} />
                        <Skeleton variant="text" width={120} height={24} />
                      </Box>
                      <Box sx={{ mt: 0.5 }}>
                        <Skeleton variant="text" width={200} height={20} />
                        <Skeleton variant="text" width={180} height={20} />
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Skeleton variant="rectangular" width={40} height={40} sx={{ borderRadius: 1 }} />
                    </TableCell>
                  </TableRow>
                ))
              ) : jobs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={2}>
                    <Alert severity="info" sx={{ my: 2 }}>
                      No jobs found. {searchDto.searchTerm || selectedTypes.length > 0 || selectedPriorities.length > 0 || selectedCustomers.length > 0 ? 'Try adjusting your search filters.' : ''}
                    </Alert>
                  </TableCell>
                </TableRow>
              ) : (
              jobs.map((job) => (
                <TableRow key={job.id} hover>
                  <TableCell>
                    <Box>
                      <Box display="flex" alignItems="center" sx={{ mb: 0.5 }}>
                        <TagIcon sx={{ fontSize: 14, mr: 0.5, color: 'text.secondary' }} />
                        <Typography variant="body2" fontWeight="medium">
                          {job.jobNumber}
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" sx={{ mb: 0.5 }}>
                        <WorkIcon sx={{ fontSize: 14, mr: 0.5, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          {jobService.getJobTypeDisplayName(job.type)}
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" sx={{ mb: 0.5 }}>
                        <EmailIcon sx={{ fontSize: 14, mr: 0.5, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          {job.customerName}
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" sx={{ mb: 0.5, gap: 0.5 }}>
                        <Chip
                          label={job.statusName}
                          size="small"
                          style={{ backgroundColor: job.statusColor, color: 'white', fontSize: '0.7rem', height: 20 }}
                        />
                        <Chip
                          label={jobService.getPriorityDisplayName(job.priority)}
                          size="small"
                          style={{ backgroundColor: jobService.getPriorityColor(job.priority), color: 'white', fontSize: '0.7rem', height: 20 }}
                        />
                      </Box>
                      <Box display="flex" alignItems="center" sx={{ mb: 0.5 }}>
                        <PersonIcon sx={{ fontSize: 14, mr: 0.5, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          {job.assignedSalesPersonName}
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" sx={{ mb: 0.5 }}>
                        <CalendarIcon sx={{ fontSize: 14, mr: 0.5, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          {new Date(job.createdDate).toLocaleDateString('en-GB')}
                          {job.expectedCompletionDate && (
                            <> - {new Date(job.expectedCompletionDate).toLocaleDateString('en-GB')}</>
                          )}
                        </Typography>
                      </Box>
                      {job.jobAddress && (
                        <Box display="flex" alignItems="center">
                          <PinDrop sx={{ fontSize: 14, mr: 0.5, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            {job.jobAddress}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    {/* Desktop Actions */}
                    <Box display={{ xs: 'none', sm: 'flex' }} gap={1} justifyContent="flex-end">
                      <Tooltip title="View Job">
                        <IconButton
                          size="small"
                          onClick={() => handleViewJobDesktop(job.id)}
                          color="primary"
                        >
                          <AssignmentIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    {/* Mobile Actions - 3 dots menu */}
                    <Box display={{ xs: 'block', sm: 'none' }}>
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, job)}
                        color="primary"
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 20, 50]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        </TableContainer>

        {/* Mobile Actions Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItem onClick={() => handleMenuAction('view')}>
            <ListItemIcon>
              <AssignmentIcon fontSize="small" />
            </ListItemIcon>
            View Job
          </MenuItem>
        </Menu>
      </Box>
    </>
  );
};

export default JobsPage;
